import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ArrowLeftRight, Users, ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Crest } from "@/components/Crest";

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = [
    { to: "/portal", label: "Overview", icon: LayoutDashboard, end: true, testid: "sidebar-nav-overview" },
    { to: "/portal/referrals", label: "Referrals", icon: ArrowLeftRight, testid: "sidebar-nav-referrals" },
    { to: "/portal/directory", label: "Directory", icon: Users, testid: "sidebar-nav-directory" },
  ];
  if (user?.role === "admin") {
    items.push({ to: "/portal/admin", label: "Access Requests", icon: ShieldCheck, testid: "sidebar-nav-admin" });
  }

  const onLogout = () => {
    logout();
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
      isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-ivory/50 hover:text-ivory border-l-2 border-transparent"
    }`;

  return (
    <div className="min-h-screen flex bg-[#F4F2EC]" data-testid="portal-layout">
      <aside className="w-60 bg-navy hidden md:flex flex-col justify-between shrink-0 min-h-screen sticky top-0">
        <div>
          <div className="p-6 border-b border-ivory/10">
            <Crest light className="h-9" />
          </div>
          <nav className="py-6 space-y-1" data-testid="portal-sidebar-nav">
            {items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass} data-testid={item.testid}>
                <item.icon size={15} strokeWidth={1.75} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="p-6 border-t border-ivory/10">
          <p className="font-serif text-base text-ivory truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-ivory/40 mt-1 truncate">
            {user?.specialty}
          </p>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="mt-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-ivory/50 hover:text-gold transition-colors"
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="md:hidden bg-navy px-5 py-4 flex items-center justify-between">
          <Crest light className="h-8" />
          <div className="flex gap-4">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`mobile-${item.testid}`}
                className={({ isActive }) =>
                  `font-mono text-[10px] tracking-[0.15em] uppercase ${isActive ? "text-gold" : "text-ivory/60"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <button onClick={onLogout} data-testid="mobile-logout-button" className="font-mono text-[10px] tracking-[0.15em] uppercase text-ivory/60">
              Out
            </button>
          </div>
        </div>
        <main className="p-6 lg:p-10 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
